import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';

import * as ramda from 'ramda';

import {convertToArray} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {getLeadsDefaultPipelineFilter} from '../../../entities/list/query/sanitize-filters-for-view';
import type {NutshellState} from '../../../store';

import {BannerCueCards} from '../../glossary/layout/banner-cue-cards';
import {FeatureGatePageWrapper} from '../../limits/feature-gates/feature-gate-page-wrapper';

import {AiGeneratedBanner} from '../banner/ai-generated-banner';
import {makeHandleClickChartToZoom} from '../charts/make-handle-click-chart-to-zoom';
import {LeadsAndActivitiesReportProvider} from '../leads-and-activities-report-provider';
import {ReportChart} from '../report-chart';
import {getReportDocumentTitle, GAP_SELECT_VALUES_DAY_TO_YEAR} from '../report-constants';
import {ReportDataSummary} from '../report-data-summary';
import type {ReportURLParams, LeadsReportKey, ChartReportParams} from '../report-types';
import {requestLeadsReportData} from '../reports-actions';
import * as reportSelectors from '../reports-selectors';

import {TasksReportSummary} from './tasks-report-summary';

import styles from '../activity/activity-report.module.css';

type TaskReportParams = {
    reportParams: ChartReportParams;
    filterParams: FilterObject;
};

type Props = {
    activeFilters: Array<FilterObject>;
    urlParams: ReportURLParams<'leads'>;
    reportKey: LeadsReportKey;
    onParamPush: (arg1: any) => any;
    onParamChange: (arg1: any, arg2: boolean) => any;
};

export function TasksReport(props: Props) {
    const {urlParams, reportKey, onParamChange, onParamPush, activeFilters} = props;

    const chartBucketCount = useSelector((state: NutshellState) =>
        reportSelectors.getCurrentReportChartBucketCount(state)
    );
    const dispatch = useDispatch();

    const prevPropsRef = React.useRef<TaskReportParams>({
        // @ts-expect-error
        reportParams: {},
        filterParams: {},
    });

    const fetchReportData = React.useCallback(
        (
            reportKeyForFetch: LeadsReportKey,
            reportParams: ChartReportParams,
            filters: Array<any>
        ) => {
            dispatch(requestLeadsReportData({reportKey: reportKeyForFetch, reportParams, filters}));
        },
        [dispatch]
    );

    React.useEffect(() => {
        const reportParams = urlParams.report;
        const filterParams = urlParams.filter;
        let filters = convertToArray(filterParams);

        const prevProps = prevPropsRef.current;

        const filtersChanged = !ramda.equals(filterParams, prevProps.filterParams);
        const gapChanged =
            reportParams.gap !== (prevProps.reportParams && prevProps.reportParams.gap);

        // If no stageset filter exists, add the default one before making the request
        const hasStagesetFilter = filters.some((filter) => filter.stageset);
        if (!hasStagesetFilter) {
            const defaultStagesetFilter = getLeadsDefaultPipelineFilter();
            filters = [...filters, defaultStagesetFilter];

            // Update URL params to reflect the default filter
            onParamChange({filter: defaultStagesetFilter}, true);
        }

        if ((gapChanged || filtersChanged) && NutClientConfig.hasTaskReport) {
            fetchReportData(reportKey, reportParams, filters);
        }

        document.title = getReportDocumentTitle(reportKey);

        prevPropsRef.current = {
            reportParams,
            filterParams,
        };
    }, [reportKey, urlParams.report, urlParams.filter, fetchReportData, onParamChange]);

    const reportParams = urlParams.report || {};
    const comparisonType = urlParams.report && urlParams.report.compareTo;
    const currentGapIndex = GAP_SELECT_VALUES_DAY_TO_YEAR.findIndex((gap) => {
        return gap.id === reportParams.gap;
    });

    const reportDataSummary = (
        <ReportDataSummary
            currentGapIndex={currentGapIndex}
            filterParams={urlParams.filter}
            reportParams={reportParams}
            reportKey={reportKey}
        />
    );

    return NutClientConfig.hasTaskReport ? (
        <div id='report-content-scroll-container' className={styles.container}>
            <div className='px-16'>
                <BannerCueCards />
            </div>

            {urlParams.report.id && (
                <div className='px-16'>
                    <AiGeneratedBanner reportId={urlParams.report.id} />
                </div>
            )}
            <LeadsAndActivitiesReportProvider>
                <ReportChart
                    useBuiltInEmptyState={true}
                    activeFilters={activeFilters}
                    // @ts-expect-error
                    onChartClick={makeHandleClickChartToZoom(
                        currentGapIndex,
                        chartBucketCount,
                        onParamPush,
                        'period'
                    )}
                    onParamChange={onParamChange}
                    reportDataSummary={reportDataSummary}
                    reportKey={reportKey}
                    reportParams={reportParams}
                    xAxisLabel='Task due date'
                />
            </LeadsAndActivitiesReportProvider>
            <TasksReportSummary filters={urlParams.filter} comparisonType={comparisonType} />
        </div>
    ) : (
        <FeatureGatePageWrapper
            header='Gain insights into your task activity'
            subHeader='Curious about your task completion metrics? Discover insights that can elevate your team’s productivity!'
            bullets={[
                'Track total tasks completed and weekly averages to stay informed about your team’s productivity.',
                'Discover which task types are completed most efficiently to optimize your workflow.',
                'Use these insights to refine your task management strategy and drive better results.',
            ]}
            feature='tasksReport'
        />
    );
}
